// iter164 — Fund Adjustment (±Rs per class) to reconcile with a cash book.
// Starting month and later months; counts in received + closing; audit-logged.
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Save } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CLASSES = [["balVatika", "Bal Vatika"], ["primary", "Primary"], ["upperPrimary", "Upper Primary"], ["highSchool", "High School"]];
const REASONS = [
  ["cash_book", "Match cash book"], ["bank_interest", "Bank interest"], ["refund", "Refund / return"],
  ["correction", "Correction of earlier entry"], ["other", "Other"],
];

export default function FundAdjustmentDialog({ fund, schoolId, year, month, onClose, onSaved }) {
  const lastDay = new Date(year, month, 0).getDate();
  const [date, setDate] = useState(`${year}-${String(month).padStart(2, "0")}-${String(Math.min(new Date().getDate(), lastDay)).padStart(2, "0")}`);
  const [amounts, setAmounts] = useState({ balVatika: "", primary: "", upperPrimary: "", highSchool: "" });
  const [reason, setReason] = useState("cash_book");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const history = fund?.fundAdjustments || [];

  const save = async () => {
    const nums = Object.fromEntries(CLASSES.map(([k]) => [k, Number(amounts[k] || 0)]));
    if (Object.values(nums).every((v) => !v)) return toast.error("Enter a non-zero amount · ରାଶି ଲେଖନ୍ତୁ");
    if (reason === "other" && !note.trim()) return toast.error("Describe the reason · କାରଣ ଲେଖନ୍ତୁ");
    setSaving(true);
    try {
      await axios.post(`${API}/fund-balances/fund-adjustment`, {
        schoolId, year, month, date, amounts: nums, reason, note: note.trim(),
      });
      toast.success("Fund adjusted · ଫଣ୍ଡ ସଂଶୋଧିତ");
      await onSaved?.();
      onClose?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[fund-adj] save failed", err?.response?.data, err);
      toast.error(err?.response?.data?.detail || "Could not save adjustment");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 sm:items-center" data-testid="fund-adj-dialog">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white p-5 pb-8 sm:rounded-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-heading text-base font-bold text-ink-900">Fund Adjustment (± Rs)</h3>
          <button type="button" onClick={onClose} data-testid="fund-adj-close"><X className="h-5 w-5" /></button>
        </div>
        <p className="mb-3 text-xs text-ink-700">Use + to add, − to remove. Counts in Received and Closing. · ଯୋଗ ପାଇଁ +, ବିୟୋଗ ପାଇଁ −</p>
        <div className="grid grid-cols-2 gap-2">
          {CLASSES.map(([k, label]) => (
            <label key={k} className="text-xs font-medium text-ink-700">{label}
              <input type="number" step="0.01" inputMode="decimal" value={amounts[k]} data-testid={`fund-adj-amount-${k}`}
                onChange={(e) => setAmounts((a) => ({ ...a, [k]: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-border px-2 py-1.5 text-sm" />
            </label>
          ))}
        </div>
        <label className="mt-3 block text-xs font-medium text-ink-700">Date
          <input type="date" min={`${year}-${String(month).padStart(2, "0")}-01`} max={`${year}-${String(month).padStart(2, "0")}-${lastDay}`}
            value={date} onChange={(e) => setDate(e.target.value)} data-testid="fund-adj-date" className="mt-1 w-full rounded-lg border border-border px-2 py-1.5 text-sm" />
        </label>
        <label className="mt-3 block text-xs font-medium text-ink-700">Reason
          <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="fund-adj-reason" className="mt-1 w-full rounded-lg border border-border px-2 py-1.5 text-sm">
            {REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (e.g. cash book page no.)" data-testid="fund-adj-note"
          className="mt-2 w-full rounded-lg border border-border px-2 py-1.5 text-sm" />
        <Button className="mt-4 w-full" disabled={saving} onClick={save} data-testid="fund-adj-save"><Save className="mr-1 h-4 w-4" /> Save adjustment</Button>
        {history.length > 0 && (
          <div className="mt-4 border-t border-border pt-3" data-testid="fund-adj-history">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink-700">History</p>
            {history.map((h) => (
              <p key={h.id} className="text-xs text-ink-800">
                {h.date} · {CLASSES.filter(([k]) => h.amounts?.[k]).map(([k, l]) => `${l} ${h.amounts[k] > 0 ? "+" : ""}${h.amounts[k]}`).join(", ")} · {(REASONS.find(([k]) => k === h.reason) || [null, h.reason])[1]}{h.note ? ` (${h.note})` : ""}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
